import React, { useEffect, useState, KeyboardEvent, useCallback, useMemo } from "react";
import {
    Box,
    Grid,
    TextField,
    Button,
    Typography,
    Chip,
    IconButton,
    InputAdornment,
    Checkbox,
    FormControlLabel,
} from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";
import DragDrop from "../../../components/drag-drop/DragDrop";
import { AutocompleteMui, MuiLoadingButton, RoundedSquareIcon, NumericFormatCustom, NumericFormat100PercentCustom } from "../../../components";
import { Program, SEO } from "../../../../core/models/db"; // Ajusta la ruta según tu estructura de carpetas
import { AddCircleOutline, Cancel, Check as CheckIcon, Close as CloseIcon } from "@mui/icons-material";
import moment from "moment";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import useDebouncedCallback from "../../../../core/hooks/@debounced-callback/useDebouncedCallback";
import ContentBlocksList from "../components/ContentBlockList";
import TemplateGeneratorButton from "../components/TemplateGenerationButton";
import { Util } from "../../../../core/util/util";
import { ContentBlock } from "../components/ContentBlockForm";
import FloatingActions from "../components/FloatingAction";


interface ProgramFormProps {
    loading?: boolean;
    initialProgram: Program; // Program con o sin SEO
    checkProgramSlug: (id: string, slug: string) => Promise<boolean>;
    onSubmit: (
        program: Program & { seo?: SEO },
        imageItem: {
            oldImage: string;
            image: string;
            nameImage: string;
        }
    ) => void;
}

const ProgramForm = ({
    loading,
    initialProgram,
    onSubmit,
    checkProgramSlug,
}: ProgramFormProps) => {
    const { t } = useTranslation();

    const [isEditing, setIsEditing] = useState(!initialProgram?.id);

    // Manejo de la imagen del Program
    const [image, setImage] = useState(initialProgram?.image || "");
    const [oldImage, setOldImage] = useState(initialProgram?.image || "");
    const [imageName, setImageName] = useState("");

    // Manejo de slug
    const [slugError, setSlugError] = useState<string | null>(null);
    const [slugChecked, setSlugChecked] = useState<null | boolean>(null);

    /**
  * Aquí tenemos los contentBlocks como un estado local
  * Se inicializa parseando el `jsonContent` que venga del initialProgram
  */
    // const [contentBlocks, setContentBlocks] = useState(() => {
    //     if (initialProgram?.jsonContent) {
    //         try {
    //             const parsed = JSON.parse(initialProgram.jsonContent);
    //             return Array.isArray(parsed) ? parsed : [];
    //         } catch (error) {
    //             console.error("Error parsing jsonContent:", error);
    //             return [];
    //         }
    //     }
    //     return [];
    // });
    const [contentBlocks, setContentBlocks] = useState<ContentBlock[]>(() => {
        if (initialProgram?.jsonContent) {
            try {
                const parsed = JSON.parse(initialProgram.jsonContent);
                if (Array.isArray(parsed)) {
                    return parsed.map((block) => ({
                        // conserva todas las propiedades existentes...
                        ...block,
                        // ...y añade un id si no lo trae ya
                        id: block.id ?? Util.getUniqueId(4),
                    }));
                }
            } catch (error) {
                console.error("Error parsing jsonContent:", error);
                return [];
            }
        }
        return [];
    });

    // Manejo de las keywords en SEO
    const [keywordChips, setKeywordChips] = useState<string[]>(
        initialProgram?.seo?.keywords
            ? initialProgram.seo.keywords.split(",").map((kw) => kw.trim()).filter(Boolean)
            : []
    );

    const [newKeyword, setNewKeyword] = useState("");
    const [keywordsError, setKeywordsError] = useState<string | null>(null);

    /**
     * =========================================================================
     *  1) DEFINIMOS EL ESQUEMA DE VALIDACIÓN (Formik + Yup)
     * =========================================================================
     */
    const validationSchema = Yup.object().shape({
        title: Yup.string().required("Título requerido").max(100, "Máximo 100 caracteres."),
        subtitle: Yup.string().max(150, "Máximo 150 caracteres."),
        description: Yup.string().max(1000, "Máximo 1000 caracteres."),
        price: Yup.string()
            .required("Precio es requerido")
            .matches(/^\d+(\.\d{1,2})?$/, "Precio inválido"),
        discountPercentage: Yup.string()
            .required("El porcentaje de descuento es requerido")
            .matches(/^\d{1,5}(\.\d{1,2})?$/, "Porcentaje inválido")
            .test("is-valid-percentage", "El porcentaje debe estar entre 0 y 100", (value) => {
                if (!value) return false;
                const num = parseFloat(value);
                return num >= 0 && num <= 100;
            }),
        externalLink: Yup.string()
            .required("URL externa es requerida")
            .transform((value) => {
                if (!value) return value;
                const trimmedValue = value.trim();
                if (
                    !trimmedValue.startsWith("http://") &&
                    !trimmedValue.startsWith("https://")
                ) {
                    return `https://${trimmedValue}`;
                }
                return trimmedValue;
            })
            .url("URL externa inválida"),
        categoryPrograms: Yup.array()
            .of(
                Yup.object().shape({
                    idCategoryFk: Yup.string().required("La categoría es requerida."),
                })
            )
            .min(1, "Debe seleccionar al menos una categoría."),
        seo: Yup.object().shape({
            slug: Yup.string()
                .max(50, "Máximo 50 caracteres")
                .matches(/^\/[a-z0-9]+\/[a-z0-9]+(?:-[a-z0-9]+)*$/, "Formato de slug inválido.")
                .required("El slug es obligatorio.")

                .test(
                    "slug-availability",
                    "Este slug ya está en uso.", // Mensaje de error personalizado
                    async (value) => {
                        if (slugError) return false;
                        return true;
                    }
                ),
            title: Yup.string().max(100, "Máximo 100 caracteres en SEO title."),
            description: Yup.string().max(300, "Máximo 300 caracteres en SEO description."),
        }),
    });
    /**
     * =========================================================================
     *  2) INICIALIZAMOS FORM + FORM VALUES
     * =========================================================================
     */
    const formik = useFormik({
        initialValues: {
            id: initialProgram?.id,
            title: initialProgram?.title || "",
            subtitle: initialProgram?.subtitle || "",
            description: initialProgram?.description || "",
            price: initialProgram?.price || "", // Agregado
            discountPercentage: initialProgram?.discountPercentage || 0, // Agregado
            externalLink: initialProgram?.externalLink || "", // Agregado
            jsonContent: initialProgram?.jsonContent || "",
            isValid: initialProgram?.isValid ?? true,
            isPublished: initialProgram?.isPublished ?? false,
            startPublicationDate: initialProgram?.startPublicationDate
                ? moment(initialProgram.startPublicationDate).toDate()
                : null,
            seo: {
                id: initialProgram?.seo?.id,
                slug: initialProgram?.seo?.slug || "",
                title: initialProgram?.seo?.title || "",
                description: initialProgram?.seo?.description || "",
                keywords: initialProgram?.seo?.keywords || "",
            },
            /**
             * Agregamos categoryPrograms en el form.
             * Asumimos que si existía antes, no queremos perder su 'id' pivot.
             * Si no hay nada, lo inicializamos como un array vacío (opcional).
             */
            categoryPrograms: initialProgram?.categoryPrograms || [],
            // 1.1) Agregamos el campo isComingSoon
            isComingSoon: initialProgram?.isComingSoon || false,
        },
        validationSchema,
        onSubmit: (values) => {
            console.log("Form values", values);
            const finalJsonContent = JSON.stringify(contentBlocks);

            // 1) Convertir array de chips a string
            const finalKeywords = keywordChips.join(",");

            // 2) Construir el Program a guardar
            const programToSave: Program & { seo?: SEO } = {
                ...values,
                jsonContent: finalJsonContent,
                image,
                startPublicationDate: values.startPublicationDate
                    ? moment(values.startPublicationDate).toDate()
                    : null,
                seo: {
                    ...values.seo,
                    keywords: finalKeywords, // Guardamos como string
                },
            };

            console.log("programToSave", programToSave);
            // return;

            onSubmit(programToSave, { oldImage, image, nameImage: values.title });
        },
        enableReinitialize: true,
    });

    // Al cambiar initialProgram, refrescar chips
    useEffect(() => {
        if (initialProgram?.seo?.keywords) {
            const chipsArr = initialProgram.seo.keywords
                .split(",")
                .map((kw) => kw.trim())
                .filter(Boolean);
            setKeywordChips(chipsArr);
        } else {
            setKeywordChips([]);
        }
    }, [initialProgram]);

    /**
     * =========================================================================
     *  3) MANEJO DE LA IMAGEN
     * =========================================================================
     */
    const handleFiles = (files: any[]) => {
        if (files.length === 0) {
            setImage("");
            formik.setFieldValue("image", "");
        } else if (files?.[0].blob) {
            const file = files[0];
            const url = URL.createObjectURL(file.blob);
            setImage(url);
            formik.setFieldValue("image", url);
        }
    };

    const changeToBackup = () => {
        setImage(null);
        setImageName("");
        setTimeout(() => {
            setImage(initialProgram?.image);
            setImageName(
                initialProgram?.image?.substring(
                    initialProgram?.image?.lastIndexOf("/") + 1
                )
            );
            setOldImage(initialProgram?.image);
        }, 200);
    };

    /**
     * =========================================================================
     *  4) BOTONES DE EDICIÓN / CANCELAR
     * =========================================================================
     */
    const handleCancelClick = () => {
        formik.resetForm();
        setContentBlocks(() => {
            // Restaurar contentBlocks iniciales:
            if (initialProgram?.jsonContent) {
                try {
                    const parsed = JSON.parse(initialProgram.jsonContent);
                    return Array.isArray(parsed) ? parsed : [];
                } catch (error) {
                    return [];
                }
            }
            return [];
        });
        setImage(initialProgram?.image || "");
        setOldImage(initialProgram?.image || "");
        setIsEditing(false);
    };

    const handleEditClick = () => {
        setIsEditing(true);
    };

    /**
     * =========================================================================
     *  5) MANEJO DE KEYWORDS (CHIPS)
     * =========================================================================
     */
    const handleAddKeyword = () => {
        const kwTrim = newKeyword.trim();
        if (!kwTrim) {
            setKeywordsError("La palabra clave no puede estar vacía.");
            return;
        }
        if (keywordChips.includes(kwTrim)) {
            setKeywordsError("Esta palabra clave ya existe en la lista.");
            return;
        }
        if (keywordChips.length >= 10) {
            setKeywordsError("No puedes añadir más de 10 palabras clave.");
            return;
        }
        const isValidKeyword = /^[a-zA-Z0-9_-]+$/.test(kwTrim);
        if (!isValidKeyword) {
            setKeywordsError(
                "Las palabras clave solo pueden contener caracteres alfanuméricos, guion medio y guion bajo."
            );
            return;
        }
        setKeywordChips((prev) => [...prev, kwTrim]);
        setNewKeyword("");
        setKeywordsError(null);
    };

    const handleRemoveKeyword = (kw: string) => {
        setKeywordChips((prev) => prev.filter((k) => k !== kw));
    };

    const handleKeywordKeyDown = (e: KeyboardEvent) => {
        if (e.key === "Enter") {
            e.preventDefault();
            handleAddKeyword();
        }
    };

    const defaultCategoryValue = useMemo(() => ({
        key: "id",
        value: initialProgram?.categoryPrograms?.[0]?.idCategoryFk || "",
    }), [initialProgram?.categoryPrograms]);

    const handleCategoryChange = useCallback((newValue) => {
        if (!newValue) {
            formik.setFieldValue("categoryPrograms", []);
        } else {
            formik.setFieldValue("categoryPrograms", [
                {
                    id: formik.values.categoryPrograms?.[0]?.id, // pivot ID si existe
                    idCategoryFk: newValue.id, // nuevo ID de categoría

                    // Por si necesitas tener a mano el objeto category
                    category: {
                        name: newValue.name,
                    },
                },
            ]);
        }
    }, [formik]);


    /**
     * =========================================================================
     *  6) MANEJO DE SLUG
     * =========================================================================
     */
    const handleSlugValidation = useDebouncedCallback(async (value: string) => {
        if (!value) return;
        const used = await checkProgramSlug(initialProgram?.seo?.id || "", value);

        setSlugChecked(!used);
        setSlugError(used ? "Este slug ya está en uso." : null);
        if (used) {
            formik.setFieldError("seo.slug", "Este slug ya está en uso.");
        } else {
            formik.setFieldError("seo.slug", undefined);
        }
    }, 300);








    /**
     * =========================================================================
     *  7) RENDER DEL FORM
     * =========================================================================
     */
    return (
        <form onSubmit={formik.handleSubmit} style={{ marginRight: 0, marginLeft: 0, padding: 0 }}>
            <Box >
                {/* <pre>{JSON.stringify(formik.errors, null, 2)}</pre> */}
                {/* DRAG & DROP PARA LA IMAGEN */}
                <DragDrop
                    id="program-image"
                    valueDefault={image}
                    disabled={loading || !isEditing}
                    directoryNameList={["program", "images"]}
                    maxSizeMB={2}
                    typeAllowed={["image/*"]}
                    isOnlyOneFile={true}
                    isBlob={true}
                    specialCase={true}
                    showFileList={false}
                    handleFiles={handleFiles}
                />

                {/* CAMPOS PRINCIPALES */}
                <Grid container spacing={2} mt={2}>
                    {/* Campo Title */}
                    <Grid item xs={12} sm={6}>
                        <TextField
                            variant="standard"
                            label={
                                <>
                                    Título <Typography component="span" color="error">*</Typography>
                                </>
                            }
                            name="title"
                            value={formik.values.title}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={Boolean(formik.touched.title && formik.errors.title)}
                            helperText={
                                formik.touched.title && typeof formik.errors.title === "string"
                                    ? formik.errors.title
                                    : undefined
                            }
                            // required
                            disabled={loading || (!isEditing && !!initialProgram?.id)}
                            fullWidth
                        />
                    </Grid>

                    {/* Campo Subtitle */}
                    <Grid item xs={12} sm={6}>
                        <TextField

                            variant="standard"
                            label="Subtítulo"
                            name="subtitle"
                            value={formik.values.subtitle}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={Boolean(formik.touched.subtitle && formik.errors.subtitle)}
                            helperText={
                                formik.touched.subtitle && typeof formik.errors.subtitle === "string"
                                    ? formik.errors.subtitle
                                    : undefined
                            }
                            disabled={loading || (!isEditing && !!initialProgram?.id)}
                            fullWidth
                        />
                    </Grid>

                    {/* Campo Description */}
                    <Grid item xs={12}>
                        <TextField
                            variant="standard"
                            label="Descripción"
                            name="description"
                            value={formik.values.description}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={Boolean(formik.touched.description && formik.errors.description)}
                            helperText={
                                formik.touched.description && typeof formik.errors.description === "string"
                                    ? formik.errors.description
                                    : undefined
                            }
                            multiline
                            rows={4}
                            disabled={loading || (!isEditing && !!initialProgram?.id)}
                            fullWidth
                        />
                    </Grid>


                    {/* <Grid item xs={12} sm={6}>
                        <TextField
                            variant="standard"
                            label={
                                <>
                                    Precio <Typography component="span" color="error">*</Typography>
                                </>
                            }
                            name="price"
                            value={formik.values.price}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={Boolean(formik.touched.price && formik.errors.price)}
                            helperText={
                                formik.touched.price && typeof formik.errors.price === "string"
                                    ? formik.errors.price
                                    : undefined
                            }
                            // required
                            disabled={loading || (!isEditing && !!initialProgram?.id)}
                            fullWidth
                            InputProps={{
                                inputComponent: NumericFormatCustom as any,
                                inputProps: {
                                    noDecimals: false,
                                    noThousandSeparator: false,
                                    maxLength: 10,
                                },
                            }}
                        />
                    </Grid>

             
                    <Grid item xs={12} sm={6}>
                        <TextField
                            variant="standard"
                            label={
                                <>
                                    Porcentaje de Descuento <Typography component="span" color="error">*</Typography>
                                </>
                            }
                            name="discountPercentage"
                            value={formik.values.discountPercentage}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={Boolean(formik.touched.discountPercentage && formik.errors.discountPercentage)}
                            helperText={
                                formik.touched.discountPercentage && typeof formik.errors.discountPercentage === "string"
                                    ? formik.errors.discountPercentage
                                    : undefined
                            }
                            // required
                            disabled={loading || (!isEditing && !!initialProgram?.id)}
                            fullWidth
                            InputProps={{
                                inputComponent: NumericFormat100PercentCustom as any,
                                inputProps: {
                                    noDecimals: true,
                                    noThousandSeparator: true,
                                    maxLength: 3,
                                },
                            }}
                        />
                    </Grid> */}




                    {/* Campo External Link */}
                    <Grid item xs={12} sm={6}>
                        <TextField
                            variant="standard"
                            label={
                                <>
                                    URL Externa <Typography component="span" color="error">*</Typography>
                                </>
                            }
                            name="externalLink"
                            value={formik.values.externalLink.replace(/^https?:\/\//, "")}
                            onChange={(e) => {
                                const value = e.target.value.trim();
                                // Evitar que el usuario agregue http:// o https:// manualmente
                                formik.setFieldValue(
                                    "externalLink",
                                    value.startsWith("http://") || value.startsWith("https://")
                                        ? value
                                        : value
                                );
                            }}
                            onBlur={(e) => {
                                const value = e.target.value.trim();
                                if (
                                    !value.startsWith("http://") &&
                                    !value.startsWith("https://")
                                ) {
                                    formik.setFieldValue("externalLink", `https://${value}`);
                                }
                                formik.handleBlur(e);
                            }}
                            error={Boolean(formik.touched.externalLink && formik.errors.externalLink)}
                            helperText={
                                formik.touched.externalLink && typeof formik.errors.externalLink === "string"
                                    ? formik.errors.externalLink
                                    : "URL externa del sponsor (sin https://, se añadirá automáticamente)"
                            }
                            // required
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Typography variant="body2" color="textSecondary">
                                            https://
                                        </Typography>
                                    </InputAdornment>
                                ),
                            }}
                            disabled={loading || (!isEditing && !!initialProgram?.id)}
                            fullWidth
                        />
                    </Grid>

                    {/* startPublicationDate con MUI X */}
                    <Grid item xs={12} sm={6}>
                        <DateTimePicker
                            slotProps={{
                                textField: {
                                    fullWidth: true,
                                    variant: "standard",
                                    size: "small",
                                },
                            }}
                            label="Fecha y hora de publicación"
                            value={
                                formik.values.startPublicationDate
                                    ? moment(formik.values.startPublicationDate)
                                    : null
                            }
                            onChange={(newValue) => {
                                formik.setFieldValue(
                                    "startPublicationDate",
                                    newValue ? newValue.toDate() : null
                                );
                            }}
                            disabled={loading || (!isEditing && !!initialProgram?.id)}
                        />
                    </Grid>


                    {/**
                 * =====================================================================
                 *  AUTOCOMPLETE para CATEGORÍA
                 *  - Solo permitimos una categoría (guardamos en categoryPrograms[0])
                 *  - Si no se selecciona nada, lo dejamos vacío (array [])
                 * =====================================================================
                 */}
                    <Grid item xs={12} sm={12}>
                        <AutocompleteMui
                            isRequired
                            placeholder={t("Seleciona una categoría")}
                            id="basic-url"
                            table="category" // el nombre de la tabla o endpoint que usas
                            disabled={!isEditing || loading}
                            /**
                             * name="categoryPrograms[0].idCategoryFk" (podríamos usarlo así)
                             * pero con este AutocompleteMui lo habitual es usar onChange para setear formik manualmente
                             */
                            defaultValue={defaultCategoryValue}
                            onChange={handleCategoryChange}
                            name={"category"}
                            error={
                                formik.touched.categoryPrograms && typeof formik.errors.categoryPrograms === "string"
                                    ? formik.errors.categoryPrograms
                                    : ""
                            }
                        />
                    </Grid>


                    <Grid item xs={12}>
                        <Box sx={{ border: '1px solid #ccc', padding: 2, borderRadius: 2, mt: 2 }}>
                            {/* Opcional: Título de la sección */}
                            {/* <Typography variant="h6" gutterBottom>Precio y Descuentos</Typography> */}

                            <Grid container spacing={2}>
                                {/* Campo Price */}
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        variant="standard"
                                        label={
                                            <>
                                                Precio <Typography component="span" color="error">*</Typography>
                                            </>
                                        }
                                        name="price"
                                        value={formik.values.price}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        error={Boolean(formik.touched.price && formik.errors.price)}
                                        helperText={
                                            formik.touched.price && typeof formik.errors.price === "string"
                                                ? formik.errors.price
                                                : undefined
                                        }
                                        disabled={loading || (!isEditing && !!initialProgram?.id)}
                                        fullWidth
                                        InputProps={{
                                            inputComponent: NumericFormatCustom as any,
                                            inputProps: {
                                                noDecimals: false,
                                                noThousandSeparator: false,
                                                maxLength: 10,
                                            },
                                        }}
                                    />
                                </Grid>

                                {/* Campo Discount Percentage */}
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        variant="standard"
                                        label={
                                            <>
                                                Porcentaje de Descuento <Typography component="span" color="error">*</Typography>
                                            </>
                                        }
                                        name="discountPercentage"
                                        value={formik.values.discountPercentage}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        error={Boolean(formik.touched.discountPercentage && formik.errors.discountPercentage)}
                                        helperText={
                                            formik.touched.discountPercentage &&
                                                typeof formik.errors.discountPercentage === "string"
                                                ? formik.errors.discountPercentage
                                                : undefined
                                        }
                                        disabled={loading || (!isEditing && !!initialProgram?.id)}
                                        fullWidth
                                        InputProps={{
                                            inputComponent: NumericFormat100PercentCustom as any,
                                            inputProps: {
                                                noDecimals: false, // Permite decimales
                                                noThousandSeparator: true,
                                                // maxLength se maneja internamente en el componente
                                            },
                                        }}
                                    />
                                </Grid>

                                {/* Nuevo Campo Calculado */}
                                <Grid item xs={12} sm={12}>
                                    <TextField
                                        variant="standard"
                                        label="Precio Final (Descuento Aplicado)"
                                        value={formik.values.price && formik.values.discountPercentage
                                            ? (
                                                parseFloat(formik.values.price) *
                                                (1 - parseFloat(formik.values.discountPercentage) / 100)
                                            ).toFixed(2)
                                            : ''}
                                        InputProps={{
                                            readOnly: true,
                                            disabled: !isEditing,
                                            style: { fontSize: '1.4rem', fontWeight: 'bold' }, // Estilo más grande y llamativo
                                        }}
                                        fullWidth
                                    />
                                </Grid>
                            </Grid>
                        </Box>
                    </Grid>


                    {/* Campo isValid -> Si se chequea es 'Meter en Reserva' */}
                    <Grid item xs={12} sm={6}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    icon={<RoundedSquareIcon checked={false} disabled={!isEditing} />}
                                    checkedIcon={<RoundedSquareIcon checked={true} disabled={!isEditing} />}
                                    disabled={loading || (!isEditing && !!initialProgram?.id)}
                                    name="isValid"
                                    checked={!formik.values.isValid}
                                    onChange={(e) => {
                                        // invertimos el valor
                                        formik.setFieldValue("isValid", !e?.target?.checked);
                                    }}
                                />
                            }
                            label="Meter en Reserva"
                        />
                    </Grid>

                    {/* 2.1) Agregamos el campo isComingSoon */}
                    <Grid item xs={12} sm={6}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    icon={<RoundedSquareIcon checked={false} disabled={!isEditing} />}
                                    checkedIcon={<RoundedSquareIcon checked={true} disabled={!isEditing} />}
                                    disabled={loading || (!isEditing && !!initialProgram?.id)}
                                    name="isComingSoon"
                                    checked={formik.values.isComingSoon}
                                    onChange={() => {
                                        formik.setFieldValue("isComingSoon", !formik?.values?.isComingSoon);
                                    }}


                                />
                            }
                            label="Próximamente"
                        />
                        <Typography variant="body2" color="text.secondary" sx={{ ml: 3 }}>
                            Este campo indica si el programa se mostrará en la web como &quot;Próximamente&quot; y sin que el visitante pueda interactuar con él.
                        </Typography>


                    </Grid>



                    {/* Campo JSON content */}
                    {/* <Grid item xs={12}>
                        <TextField
                            variant="standard"
                            label="Contenido (JSON)"
                            name="jsonContent"
                            value={formik.values.jsonContent}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            multiline
                            rows={2}
                            fullWidth
                            disabled={loading || (!isEditing && !!initialProgram?.id)}
                        />
                    </Grid> */}
                </Grid>


                {/* Aquí renderizamos la lista de bloques */}
                <Box mt={4}>
                    <Box display="flex" alignItems="center" justifyContent="space-between" sx={{
                        borderBottom: 1,
                        borderColor: 'divider',
                        paddingBottom: 1,
                        marginBottom: 2
                    }}>
                        <Typography variant="h6" gutterBottom>
                            Bloques de Contenido
                        </Typography>
                        {/* Botón para generar plantilla */}
                        {/* <TemplateGeneratorButton disabled={!isEditing} onGenerateTemplate={setContentBlocks} /> */}
                    </Box>
                    <ContentBlocksList
                        blocks={contentBlocks}
                        setBlocks={setContentBlocks}
                        disabled={loading || (!isEditing && !!initialProgram?.id)}
                    />
                </Box>


                {/* APARTADO SEO */}
                <Grid container spacing={2} mt={4}>
                    <Grid item xs={12}>
                        <div className="text-2xl font-medium mb-4">SEO</div>
                    </Grid>

                    {/* Campo Slug */}
                    <Grid item xs={12} sm={6}>
                        <TextField
                            variant="standard"
                            label="Slug"
                            name="seo.slug"
                            value={formik.values.seo.slug.replace(/^\/programas\//, "")} // Mostrar sin el prefijo
                            onChange={(e) => {
                                let inputValue = e.target.value;

                                // Quitar el prefijo "/programas/" si el usuario lo escribe manualmente
                                inputValue = inputValue.replace(/^\/programas\//, "");

                                // Internamente almacenamos el prefijo "/programas/"
                                const prefixedValue = `/programas/${inputValue}`;
                                formik.setFieldValue("seo.slug", prefixedValue);

                                // Validar el slug con debounce
                                handleSlugValidation(prefixedValue);
                            }}
                            onBlur={(e) => {
                                let inputValue = formik.values.seo.slug;

                                // Asegurar que el prefijo "/programas/" siempre esté presente
                                if (!inputValue.startsWith("/programas/")) {
                                    inputValue = `/programas/${inputValue}`;
                                }

                                formik.setFieldValue("seo.slug", inputValue);
                                formik.handleBlur(e);
                            }}
                            error={Boolean(formik.touched.seo?.slug && formik.errors.seo?.slug)}
                            helperText={
                                formik.touched.seo?.slug && typeof formik.errors.seo?.slug === "string"
                                    ? formik.errors.seo?.slug
                                    : "Identificador único en la URL, como '/mi-titulo-seo'."
                            }
                            // required
                            disabled={loading || (!isEditing && !!initialProgram?.id)}
                            fullWidth
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Typography variant="body2" color="textSecondary">
                                            /programas/
                                        </Typography>
                                    </InputAdornment>
                                ),
                                endAdornment: (
                                    <InputAdornment position="end">

                                        {isEditing && slugChecked !== null && (

                                            <IconButton edge="end">
                                                {slugChecked && !formik?.errors?.seo?.slug ? (
                                                    <CheckIcon color="success" />
                                                ) : (
                                                    <CloseIcon color="error" />
                                                )}
                                            </IconButton>
                                        )}

                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Grid>

                    {/* Campo Título SEO */}
                    <Grid item xs={12} sm={6}>
                        <TextField
                            variant="standard"
                            label="SEO Title"
                            name="seo.title"
                            value={formik.values.seo.title}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={Boolean(formik.touched.seo?.title && formik.errors.seo?.title)}
                            helperText={
                                formik.touched.seo?.title && typeof formik.errors.seo?.title === "string"
                                    ? formik.errors.seo?.title
                                    : undefined
                            }
                            disabled={loading || (!isEditing && !!initialProgram?.id)}
                            fullWidth
                        />
                    </Grid>

                    {/* Campo Description SEO */}
                    <Grid item xs={12}>
                        <TextField
                            variant="standard"
                            label="SEO Description"
                            name="seo.description"
                            value={formik.values.seo.description}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={Boolean(formik.touched.seo?.description && formik.errors.seo?.description)}
                            helperText={
                                formik.touched.seo?.description &&
                                    typeof formik.errors.seo?.description === "string"
                                    ? formik.errors.seo?.description
                                    : undefined
                            }
                            disabled={loading || (!isEditing && !!initialProgram?.id)}
                            multiline
                            rows={3}
                            fullWidth
                        />
                    </Grid>

                    {/* Campo Keywords - Chips */}
                    <Grid item xs={12}>
                        <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
                            Palabras clave (Keywords)
                        </Typography>
                        <Box display="flex" gap={1}>
                            <TextField
                                variant="standard"
                                label="Nueva palabra clave"
                                value={newKeyword}
                                onChange={(e) => setNewKeyword(e.target.value)}
                                onKeyDown={handleKeywordKeyDown}
                                disabled={loading || (!isEditing && !!initialProgram?.id)}
                            />
                            <div className="flex items-end mt-2.5">
                                <IconButton
                                    color="primary"
                                    onClick={handleAddKeyword}
                                    disabled={!newKeyword.trim() || loading || (!isEditing && !!initialProgram?.id)}
                                >
                                    <AddCircleOutline />
                                </IconButton>
                            </div>
                        </Box>
                        <Box mt={1} display="flex" flexWrap="wrap" gap={1}>
                            {keywordChips.map((kw) => (
                                <Chip
                                    key={kw}
                                    label={kw}
                                    onDelete={() => handleRemoveKeyword(kw)}
                                    deleteIcon={<Cancel />}
                                    disabled={loading || (!isEditing && !!initialProgram?.id)}
                                />
                            ))}
                        </Box>
                        {keywordsError && (
                            <Typography variant="body2" color="error" className="mt-2">
                                {keywordsError}
                            </Typography>
                        )}
                    </Grid>
                </Grid>

                {/* BOTONES DE ACCIÓN */}
                <FloatingActions
                    isNew={!initialProgram?.id}
                    isEditing={isEditing}
                    loading={loading || false}
                    isValid={formik.isValid}
                    slugChecked={slugChecked}
                    onCreate={() => formik.submitForm()}
                    onEdit={handleEditClick}
                    onCancel={handleCancelClick}
                    onSave={() => formik.submitForm()}
                />
                {/* <Box display="flex" justifyContent="flex-end" gap={2} mt={5}>
                    {!initialProgram?.id && (
                        <div className="w-auto">
                            <MuiLoadingButton
                                title="Crear"
                                variant="contained"
                                color="primary"
                                type="submit"
                                disabled={!formik.isValid || loading || slugChecked === false}
                                isLoading={loading}
                            />
                        </div>
                    )}
                    {initialProgram?.id && !isEditing && (
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleEditClick}
                            disabled={loading}>
                            Editar
                        </Button>
                    )}
                    {initialProgram?.id && isEditing && (
                        <div className="flex flex-row gap-2">
                            <Button
                                className="flex-1"
                                variant="outlined"
                                color="primary"
                                onClick={handleCancelClick}
                                disabled={loading}
                            >
                                Cancelar
                            </Button>
                            <div className="w-auto">
                                <MuiLoadingButton
                                    title="Guardar"
                                    variant="contained"
                                    color="primary"
                                    type="submit"
                                    disabled={!formik.isValid || loading || slugChecked === false}
                                    isLoading={loading}
                                />
                            </div>
                        </div>
                    )}
                </Box> */}
            </Box>
        </form>
    );
};

export default ProgramForm;